import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import usePartnerActivateKit from 'services/kits/usePartnerActivateKit';
import { EMAIL_VALIDATION_PATTERN } from 'assets';
import {
  ButtonColors,
  ButtonSizes,
  DesignButton,
  THField,
  Typography,
} from 'components';
import { TextLink } from '@repo/ui';
import type { InformationScreenProps } from '../../types';

import styles from './InformationScreens.module.scss';

type EmailFormValues = {
  email: string;
  terms: boolean;
};

export const EmailScreen = ({
  setActivationState,
  activationState,
  step,
  setCurrentActivationStep,
}: InformationScreenProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<EmailFormValues>({
    mode: 'onTouched',
  });
  const partnerActivateKit = usePartnerActivateKit();

  const emailFormField = register('email', {
    required: { value: true, message: 'Please enter your email' },
    pattern: EMAIL_VALIDATION_PATTERN,
  });

  const onSubmitEmail = async (formData: EmailFormValues) => {
    try {
      const { kit: responseKit, tinyaccount: responseTinyAccount } =
        await partnerActivateKit.mutateAsync({
          kit: activationState.kit,
          tinyAccount: {
            ...activationState.tinyAccount,
            email: formData.email,
          },
        });

      const updatedKit = {
        ...activationState.kit,
        tinyaccount_id: responseKit.tinyaccount_id,
      };
      const updatedTinyAccount = {
        ...activationState.tinyAccount,
        id: responseTinyAccount.id,
        email: formData.email,
      };
      setActivationState({
        kit: updatedKit,
        tinyAccount: updatedTinyAccount,
      });

      if (typeof step === 'number' && !!setCurrentActivationStep) {
        const shouldSkipSurveys =
          updatedKit.partner?.portal_config?.skip_surveys ||
          updatedKit.partner?.portal_config?.survey_type === 'skip';
        if (shouldSkipSurveys) {
          setCurrentActivationStep(step + 2);
        } else {
          setCurrentActivationStep(step + 1);
        }
      }
    } catch (error) {
      toast.error('Failed to activate kit. Please try again.');
    }
  };
  console.log(errors.email);
  return (
    <form
      className='flex flex-column gap-2'
      onSubmit={handleSubmit(onSubmitEmail)}
    >
      <Typography variant='heading-l'>What is your email?</Typography>
      <div className='mb-4'>
        <THField
          type='email'
          label='Email'
          icon='mail'
          error={errors.email}
          {...emailFormField}
        />

        <div className='flex flex-column gap-3'>
          <Typography variant='label'>
            By activating this kit, you acknowledge and agree to abide by Tiny
            Health’s Terms and Privacy Policy, as well as the Terms and Privacy
            Policy of the partner or organization from which you purchased your
            test. Please review all applicable documents before proceeding.
          </Typography>

          <div className={'flex align-items-center gap-2'}>
            <input
              type='checkbox'
              className={styles.consentCheckbox}
              {...register('terms', { required: true })}
              aria-required='true'
            />
            <Typography variant='body-s'>
              By continuing, you certify that you have read, understand, and
              agree to Tiny Health’s{' '}
              <TextLink
                to='https://www.tinyhealth.com/terms'
                label='Terms'
                color='purple'
                size='body-s'
                external
              />{' '}
              and{' '}
              <TextLink
                to='https://www.tinyhealth.com/privacy-policy'
                label='Privacy Policy'
                color='purple'
                size='body-s'
                external
              />
            </Typography>
          </div>
        </div>
        {errors.terms && (
          <Typography variant='label' className={styles.formError}>
            This is required.
          </Typography>
        )}
      </div>
      <DesignButton
        label='Continue'
        type='submit'
        loading={partnerActivateKit.isPending}
        disabled={!isValid}
        size={ButtonSizes.M}
        color={ButtonColors.PURPLE}
        width={'full'}
        className='mt-1'
      />
    </form>
  );
};
